import React, { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useSiteStore } from '@core/site/useSiteStore';
import { useClientStore } from '@core/client/useClientStore';
import {
  Address,
  Site,
  SiteInput,
  SiteStatus,
} from '@core/generated/gql-types';
import { generateFriendlyAddress } from '@core/util/google-places.service';
import useSiteAssignment, {
  getUserDisplayName,
} from '@/hooks/useSiteAssignment';
import AddressForm from '../forms/AddressForm';
import SingleSelect from '../shared/SingleSelect';
import Button from '../Button';

interface ClientSiteDetailsProps {
  site: Site;
  clientId: string;
  onBack: () => void;
}

const SITE_STATUS_OPTIONS: SiteStatus[] = [
  SiteStatus.Active,
  SiteStatus.Inactive,
];

const formatSiteStatusLabel = (status: SiteStatus) => {
  switch (status) {
    case SiteStatus.Active:
      return 'ENABLED';
    case SiteStatus.Inactive:
      return 'DISABLED';
    default:
      return status;
  }
};

const EMPTY_ADDRESS: Address = {
  addressLine1: '',
  addressLine2: '',
  suburb: '',
  state: '',
  postcode: '',
  countryLabel: '',
  countryValue: '',
};

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Site name is required')
    .max(100, 'Name must be at most 100 characters'),
  address: Yup.object()
    .nullable()
    .test('address-required', 'Address is required', (value) => {
      return value !== null && value !== undefined;
    })
    .shape({
      addressLine1: Yup.string().required('Address is required'),
      suburb: Yup.string().required('Suburb is required'),
      state: Yup.string().required('State is required'),
      postcode: Yup.string().required('Postcode is required'),
      countryLabel: Yup.string().required('Country is required'),
    }),
});

const ClientSiteDetails: React.FC<ClientSiteDetailsProps> = ({
  site,
  clientId,
  onBack,
}) => {
  const { updateSite, setSiteStatus, setAccountsForSite } = useSiteStore();
  const { fetchUsersByClient, fetchInvitesForClient } = useClientStore();

  const [isEditing, setIsEditing] = useState(false);
  const [isManualAddressEntry, setIsManualAddressEntry] = useState(false);

  const {
    siteAssignmentOptions,
    allAssignableOptions,
    selectedUserIds,
    selectedInviteIds,
    selectedOptionsCount,
    toggleSelectedUser,
    toggleSelectedInvite,
    selectAllAssignable,
    clearSelection,
    resetSelectionToAssigned,
  } = useSiteAssignment({ clientId, siteId: site.id });

  const startEditing = () => {
    resetSelectionToAssigned();
    setIsEditing(true);
  };

  const initialAddress: Address = useMemo(
    () =>
      site.address
        ? {
            addressLine1: site.address.addressLine1 ?? '',
            addressLine2: site.address.addressLine2 ?? '',
            suburb: site.address.suburb ?? '',
            state: site.address.state ?? '',
            postcode: site.address.postcode ?? '',
            countryLabel: site.address.countryLabel ?? '',
            countryValue: site.address.countryValue ?? '',
          }
        : EMPTY_ADDRESS,
    [site],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: site.name ?? '',
      status: site.status ?? SiteStatus.Active,
      address: initialAddress,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, setStatus }) => {
      setSubmitting(true);
      setStatus(undefined);

      try {
        const payload: SiteInput = {
          name: values.name,
          clientId,
          address: values.address,
          customAttributes: [],
        };

        const updateResult = await updateSite(site.id, payload);
        if (!updateResult.success) {
          setStatus(updateResult.error ?? 'Failed to update site');
          return;
        }

        if (values.status !== site.status) {
          const statusResult = await setSiteStatus(site.id, values.status);
          if (!statusResult.success) {
            setStatus(statusResult.error ?? 'Failed to update site status');
            return;
          }
        }

        const usersResult = await setAccountsForSite(
          site.id,
          selectedUserIds,
          selectedInviteIds,
        );
        if (!usersResult.success) {
          setStatus(usersResult.error ?? 'Failed to update site accounts');
          return;
        }

        await Promise.all([
          fetchUsersByClient(clientId),
          fetchInvitesForClient(clientId),
        ]);

        setIsManualAddressEntry(false);
        setIsEditing(false);
      } catch (error) {
        setStatus(
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleCancel = () => {
    if (formik.isSubmitting) return;
    formik.resetForm();
    resetSelectionToAssigned();
    setIsManualAddressEntry(false);
    setIsEditing(false);
  };

  const addressFieldErrors = useMemo(() => {
    const errors = formik.errors.address;
    if (!errors || typeof errors === 'string') return null;
    return errors;
  }, [formik.errors.address]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
        <h2 className="text-lg font-semibold text-gray-800">{site.name}</h2>
        <div className="flex items-center gap-4">
          {!isEditing && (
            <Button
              type="button"
              variant="text"
              onClick={startEditing}
              className="gap-2 px-0 py-0 text-sm font-medium"
            >
              <FontAwesomeIcon icon={faPenToSquare} className="h-4 w-4" />
              Edit
            </Button>
          )}
          <Button
            type="button"
            variant="text"
            onClick={onBack}
            className="px-0 py-0 text-gray-400 hover:text-gray-600"
          >
            <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {isEditing ? (
        <form onSubmit={formik.handleSubmit} className="space-y-6 p-6">
          {formik.status && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600">
              {formik.status}
            </div>
          )}

          <div>
            <label
              className="mb-1 block text-sm font-medium text-gray-700"
              htmlFor="site-name"
            >
              Site Name *
            </label>
            <input
              type="text"
              id="site-name"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`w-full rounded-md border bg-white px-4 py-2 focus:border-blue-500 focus:ring-blue-500 ${
                formik.touched.name && formik.errors.name
                  ? 'border-red-300'
                  : 'border-gray-300'
              }`}
            />
            {formik.touched.name && formik.errors.name && (
              <div className="mt-1 text-sm text-red-600">
                {formik.errors.name}
              </div>
            )}
          </div>

          <div>
            <AddressForm
              onAddressChange={(address) =>
                formik.setFieldValue('address', address)
              }
              initialAddress={formik.values.address}
              onManualEntryChange={setIsManualAddressEntry}
            />
            {!isManualAddressEntry && (
              <>
                {formik.touched.address &&
                  typeof formik.errors.address === 'string' && (
                    <div className="mt-1 text-sm text-red-600">
                      {formik.errors.address}
                    </div>
                  )}
                {formik.touched.address && addressFieldErrors?.addressLine1 && (
                  <div className="mt-1 text-sm text-red-600">
                    {addressFieldErrors.addressLine1}
                  </div>
                )}
              </>
            )}
            {isManualAddressEntry && (
              <>
                {formik.touched.address?.addressLine1 &&
                  addressFieldErrors?.addressLine1 && (
                    <div className="mt-1 text-sm text-red-600">
                      {addressFieldErrors.addressLine1}
                    </div>
                  )}
                {formik.touched.address?.suburb &&
                  addressFieldErrors?.suburb && (
                    <div className="mt-1 text-sm text-red-600">
                      {addressFieldErrors.suburb}
                    </div>
                  )}
                {formik.touched.address?.state && addressFieldErrors?.state && (
                  <div className="mt-1 text-sm text-red-600">
                    {addressFieldErrors.state}
                  </div>
                )}
                {formik.touched.address?.postcode &&
                  addressFieldErrors?.postcode && (
                    <div className="mt-1 text-sm text-red-600">
                      {addressFieldErrors.postcode}
                    </div>
                  )}
                {formik.touched.address?.countryLabel &&
                  addressFieldErrors?.countryLabel && (
                    <div className="mt-1 text-sm text-red-600">
                      {addressFieldErrors.countryLabel}
                    </div>
                  )}
              </>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Status
            </label>
            <SingleSelect<SiteStatus>
              options={SITE_STATUS_OPTIONS}
              value={formik.values.status}
              onChange={(value) => formik.setFieldValue('status', value)}
              optionLabel={formatSiteStatusLabel}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Users
            </label>
            <p className="mb-2 text-sm text-gray-500">
              Choose which users can view and post jobs to this site.
            </p>

            {siteAssignmentOptions.length === 0 ? (
              <p className="text-sm text-gray-500">
                No client users available for this client.
              </p>
            ) : (
              <>
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="text-gray-500">
                    {selectedOptionsCount} of {siteAssignmentOptions.length}{' '}
                    selected
                  </span>
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="text"
                      onClick={selectAllAssignable}
                      className="px-0 py-0 font-medium"
                    >
                      Select all
                    </Button>
                    <Button
                      type="button"
                      variant="text"
                      onClick={clearSelection}
                      className="px-0 py-0 font-medium"
                    >
                      Clear
                    </Button>
                  </div>
                </div>

                <div className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
                  {siteAssignmentOptions.map((option) =>
                    option.type === 'user' ? (
                      <label
                        key={`user-${option.id}`}
                        className="flex cursor-pointer items-start gap-3 px-4 py-3 hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(option.id)}
                          onChange={() => toggleSelectedUser(option.id)}
                          className="mt-0.5 h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="flex flex-col">
                          <span className="text-sm text-gray-900">
                            {getUserDisplayName(option.user) ? (
                              <div className="flex flex-col items-start gap-1">
                                <span className="text-sm font-medium text-gray-900">
                                  {getUserDisplayName(option.user)}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {option.user.email}
                                </span>
                              </div>
                            ) : (
                              <div className="flex flex-col items-start gap-1">
                                <span className="mt-0.5 inline-flex w-fit items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                                  Invited
                                </span>
                                <span className="text-sm text-gray-900">
                                  {option.email}
                                </span>
                              </div>
                            )}
                          </span>
                        </span>
                      </label>
                    ) : (
                      <label
                        key={`invite-${option.id}`}
                        className="flex cursor-pointer items-start gap-3 px-4 py-3 hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={selectedInviteIds.includes(option.id)}
                          onChange={() => toggleSelectedInvite(option.id)}
                          className="mt-0.5 h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="flex flex-col">
                          <span className="mt-0.5 inline-flex w-fit items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                            Invited
                          </span>
                          <span className="text-xs text-gray-500">
                            {option.invite.email}
                          </span>
                        </span>
                      </label>
                    ),
                  )}
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-3 border-t pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={formik.isSubmitting}
              className="px-4 py-2 text-sm font-medium"
            >
              {formik.isSubmitting ? 'Saving...' : 'Done'}
            </Button>
          </div>
        </form>
      ) : (
        <dl className="divide-y divide-gray-100">
          <div className="flex items-start justify-between px-6 py-4">
            <dt className="text-sm text-gray-500">Name</dt>
            <dd className="text-right text-sm font-medium text-gray-900">
              {site.name}
            </dd>
          </div>
          <div className="flex items-start justify-between px-6 py-4">
            <dt className="text-sm text-gray-500">Address</dt>
            <dd className="max-w-[60%] text-right text-sm font-medium text-gray-900">
              {generateFriendlyAddress(site.address ?? {}) || '-'}
            </dd>
          </div>
          <div className="flex items-start justify-between px-6 py-4">
            <dt className="text-sm text-gray-500">Status</dt>
            <dd className="text-right text-sm font-medium text-gray-900">
              {site.status === SiteStatus.Active ? 'ENABLED' : 'DISABLED'}
            </dd>
          </div>
          <div className="flex items-start justify-between px-6 py-4">
            <dt className="text-sm text-gray-500">Users</dt>
            <dd className="max-w-[60%] text-right text-sm font-medium text-gray-900">
              {allAssignableOptions.length === 0 &&
              allAssignableOptions.length === 0 ? (
                '-'
              ) : (
                <ul className="space-y-1">
                  {allAssignableOptions.map((user) =>
                    user.type === 'user' ? (
                      <li key={user.id}>
                        {getUserDisplayName(user.user) ? (
                          <span className="text-sm font-medium text-gray-900">
                            {getUserDisplayName(user.user)} - {user.user.email}
                          </span>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            <span className="mt-0.5 inline-flex w-fit items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                              Invited
                            </span>
                            <li key={user.id}> - {user.email}</li>
                          </div>
                        )}
                      </li>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <span className="mt-0.5 inline-flex w-fit items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                          Invited
                        </span>
                        <li key={user.id}> - {user.invite.email}</li>
                      </div>
                    ),
                  )}
                </ul>
              )}
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
};

export default ClientSiteDetails;
